import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import { track } from '@vercel/analytics';
import './styles/ResultAndRevision.scss';
import logger from './utils/logger';

// Add error modal component
const ErrorModal = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <p>{message}</p>
        <div className="modal-buttons">
          <button 
            className="modal-button cancel-button" 
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * parseLineByLine(text, category)
 *
 * Reads the entire "result" string line-by-line and splits it into paragraphs + feedback.
 */
function parseLineByLine(text, category) {
  // Decide which feedback codes to look for:
  let validCodes = '[PCLM]'; // Default English Leaving Cert
  if (isGCSECategory(category)) {
    validCodes = '[ALCM]'; // English GCSE
  } else if (isGCSELanguageCategory(category)) {
    validCodes = '[CLA]'; // GCSE Languages (Content, Language, Accuracy)
  } else if (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral') {
    validCodes = '[GCS]'; // Irish
  } else if (
    category.startsWith('FR_') ||
    category.startsWith('ES_')
  ) {
    validCodes = '[CL]';  // French/Spanish = Communication & Language
  } else if (category.startsWith('DE_')) {
    validCodes = '[CE]';  // German = Content & Expression
  }

  // We'll create a dynamic regex that checks for {X: ...}, 
  // where X is one of G,C,S if Aiste, or P,C,L,M otherwise:
  const isSingleBracket = (line) => {
    const pattern = new RegExp(`^\\{${validCodes}:`, 'i');
    // Check if line starts with a pattern like {P: or if it contains [TEACHER] tag
    return pattern.test(line.trim()) || 
           (line.trim().includes('[TEACHER]') && line.trim().includes('}'));
  };

  const lines = text.split('\n');
  const paragraphs = [];
  let currentParagraph = null;

  for (let rawLine of lines) {
    const lineTrim = rawLine.trim();
    if (!lineTrim) continue;

    if (isSingleBracket(lineTrim)) {
      // This line is feedback
      if (currentParagraph) {
        currentParagraph.feedback.push(rawLine);
      }
    } else {
      // Paragraph text or leftover short text
      if (lineTrim.length > 5) {
        if (currentParagraph && currentParagraph.text.trim().length > 0) {
          paragraphs.push(currentParagraph);
          currentParagraph = {
            text: rawLine,
            feedback: []
          };
        } else if (currentParagraph) {
          currentParagraph.text = rawLine;
        } else {
          currentParagraph = {
            text: rawLine,
            feedback: []
          };
        }
      } else {
        // lineTrim <= 5 => leftover short text
        if (!currentParagraph) {
          currentParagraph = { text: rawLine, feedback: [] };
        } else {
          currentParagraph.text += '\n' + rawLine;
        }
      }
    }
  }
  if (currentParagraph && currentParagraph.text.trim().length > 0) {
    paragraphs.push(currentParagraph);
  }
  // Merge short paragraphs (< 50 words, no feedback) with their subsequent paragraph
  for (let i = 0; i < paragraphs.length - 1; i++) {
    const paragraph = paragraphs[i];
    const wordCount = paragraph.text.trim().split(/\s+/).length;
    if (wordCount < 150 && paragraph.feedback.length === 0) {
      // Merge paragraph i into paragraph i+1
      paragraphs[i+1].text = paragraph.text + "\n\n" + paragraphs[i+1].text;
      // Remove paragraph i from the array
      paragraphs.splice(i, 1);
      // Step back one index so we don't skip the newly-shifted paragraph
      i--;
    }
  }

  return paragraphs;
}

/**
 * processInlineFeedback(text, category)
 * Converts inline annotations into colored spans and block feedback into styled divs.
 */
function processInlineFeedback(text, category) {
  if (!text) return '';
  let processed = text.replace(/\n{3,}/g, '\n\n');

  // Define color maps for English scheme or Aiste scheme
  const colorMapEnglish = {
    'P': '#2ecc71', // Purpose
    'C': '#e74c3c', // Coherence
    'L': '#3498db', // Language
    'M': '#f1c40f'  // Mechanics
  };
  const colorMapGCSE = {
    'A': '#9b59b6', // Argument (purple)
    'L': '#3498db', // Language (blue) 
    'C': '#f39c12', // Context (orange)
    'M': '#e74c3c'  // Mechanics (red)
  };
  const colorMapAiste = {
    'G': '#16a085', // Gaeilge
    'C': '#c0392b', // Content
    'S': '#8e44ad'  // Style
  };
  const colorMapFrench = {
    'C': '#FF6B6B', // Communication
    'L': '#4ECDC4'  // Language
  };
  const colorMapGerman = {
    'C': '#2ecc71', // Content
    'E': '#3498db'  // Expression
  };
  const colorMapGCSELang = {
    'C': '#f39c12', // Content
    'L': '#3498db', // Language
    'A': '#e74c3c'  // Accuracy
  };

  // Decide which map to use
  let colorMap = colorMapEnglish;
  if (isGCSECategory(category)) {
    colorMap = colorMapGCSE;
  } else if (isGCSELanguageCategory(category)) {
    colorMap = colorMapGCSELang;
  } else if (
    category === 'Aiste' ||
    category === 'Grammar Checker' ||
    category.startsWith('Filiocht_') ||
    category === 'IE_Oral'
  ) {
    colorMap = colorMapAiste;
  } else if (
    category.startsWith('FR_') ||
    category.startsWith('ES_')
  ) {
    colorMap = colorMapFrench;
  } else if (category.startsWith('DE_')) {
    colorMap = colorMapGerman;
  }

  // Decide which codes to look for:
  let codeSet = '[PCLM]';
  if (isGCSECategory(category)) {
    codeSet = '[ALCM]';
  } else if (isGCSELanguageCategory(category)) {
    codeSet = '[CLA]';
  } else if (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral') {
    codeSet = '[GCS]';
  } else if (category.startsWith('FR_') || category.startsWith('ES_')) {
    codeSet = '[CL]';
  } else if (category.startsWith('DE_')) {
    codeSet = '[CE]';
  }

  // Double-bracket pattern, e.g. {{G: text}} or {{P: text}}
  const doubleBracketRegex = new RegExp(`\\{\\{(${codeSet}):\\s*([^}]+?)\\}\\}`, 'g');
  processed = processed.replace(doubleBracketRegex, (match, type, content) => {
    return `<span style="background-color: ${colorMap[type]}33; border-bottom: 2px solid ${colorMap[type]};">${content}</span>`;
  });

  // Single-bracket pattern, e.g. {G: text} or {P: text}
  const singleBracketRegex = new RegExp(`\\{(${codeSet}):\\s*([^}]+?)\\}`, 'g');
  processed = processed.replace(singleBracketRegex, (match, type, content) => {
    return `<div style="margin: 5px 0; padding: 8px; background-color: ${colorMap[type]}22; border-left: 4px solid ${colorMap[type]};">${content}</div>`;
  });

  return processed;
}

/**
 * stripInlineAnnotations(text)
 * Removes double bracket annotations entirely from text; single bracket also removed if needed.
 */
function stripInlineAnnotations(text) {
  return text.replace(/\{\{[PCLMGSE]:.*?\}\}/g, '').replace(/\\n/g, '\n');
}

/**
 * FeedbackLegend - A small component to show the color legend for the feedback types.
 */
function FeedbackLegend({ category }) {
  if (isGCSECategory(category)) {
    return (
      <div style={{
        display: 'flex', gap: '15px', justifyContent: 'center',
        margin: '10px 0', padding: '10px', backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <span style={{ color: '#9b59b6' }}>● Argument</span>
        <span style={{ color: '#3498db' }}>● Language</span>
        <span style={{ color: '#f39c12' }}>● Context</span>
        <span style={{ color: '#e74c3c' }}>● Mechanics</span>
      </div>
    );
  } else if (category === 'Aiste' || category === 'Grammar Checker'|| category.startsWith('Filiocht_') || category === 'IE_Oral') {
    // Show Aiste legend for "G, C, S"
    return (
      <div style={{
        display: 'flex', gap: '15px', justifyContent: 'center',
        margin: '10px 0', padding: '10px', backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <span style={{ color: '#16a085' }}>● Gaeilge</span>
        <span style={{ color: '#c0392b' }}>● Content</span>
        <span style={{ color: '#8e44ad' }}>● Style</span>
      </div>
    );
  } else if (category.startsWith('FR_') || category.startsWith('ES_')) {
    // Show French/Spanish legend
    return (
      <div style={{
        display: 'flex', gap: '15px', justifyContent: 'center',
        margin: '10px 0', padding: '10px', backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <span style={{ color: '#FF6B6B' }}>● Communication</span>
        <span style={{ color: '#4ECDC4' }}>● Language</span>
      </div>
    );
  } else if (category.startsWith('DE_')) {
    // German => E for Expression, C for Content
    return (
      <div style={{
        display: 'flex', gap: '15px', justifyContent: 'center',
        margin: '10px 0', padding: '10px', backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <span style={{ color: '#2ecc71' }}>● Content (C)</span>
        <span style={{ color: '#3498db' }}>● Expression (E)</span>
      </div>
    );
  } else if (isGCSELanguageCategory(category)) {
    // Show GCSE Language legend (C, L, A)
    return (
      <div style={{
        display: 'flex', gap: '15px', justifyContent: 'center',
        margin: '10px 0', padding: '10px', backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <span style={{ color: '#f39c12' }}>● Content (C)</span>
        <span style={{ color: '#3498db' }}>● Language (L)</span>
        <span style={{ color: '#e74c3c' }}>● Accuracy (A)</span>
      </div>
    );
  } else {
    // Original scheme for English => P, C, L, M
    return (
      <div style={{
        display: 'flex', gap: '15px', justifyContent: 'center',
        margin: '10px 0', padding: '10px', backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <span style={{ color: '#2ecc71' }}>● Purpose</span>
        <span style={{ color: '#e74c3c' }}>● Coherence</span>
        <span style={{ color: '#3498db' }}>● Language Use</span>
        <span style={{ color: '#f1c40f' }}>● Mechanics</span>
      </div>
    );
  }
}

// First, add the same style objects at the top of the component
const commonInputStyle = {
  width: '100%',
  padding: '12px 16px',
  marginBottom: '15px',
  borderRadius: '8px',
  border: '1px solid #cbd5e1',
  fontSize: '16px',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  backgroundColor: 'white',
  color: '#334155',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  resize: 'vertical'
};

const inputHoverStyle = {
  borderColor: '#94a3b8',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
};

const inputFocusStyle = {
  outline: 'none',
  borderColor: '#2980b9',
  boxShadow: '0 0 0 3px rgba(41, 128, 185, 0.1)'
};

// First, add these style objects near the top with your other styles:
const commonButtonStyle = {
  padding: '12px 24px',
  fontSize: '16px',
  backgroundColor: '#2980b9',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  margin: '0 4px'
};

const secondaryButtonStyle = {
  ...commonButtonStyle,
  backgroundColor: '#95a5a6'
};

////////////////////////////////////////////////////////////////////////////////
// 1. Define color maps for single-bracket comments:
////////////////////////////////////////////////////////////////////////////////
const colorMapEnglish = {
  'P': '#2ecc71', // Purpose
  'C': '#e74c3c', // Coherence
  'L': '#3498db', // Language
  'M': '#f1c40f'  // Mechanics
};
const colorMapAiste = {
  'G': '#16a085', // Gaeilge
  'C': '#c0392b', // Content
  'S': '#8e44ad'  // Style
};
const colorMapGCSE = {
  'A': '#9b59b6', // Argument (purple)
  'L': '#3498db', // Language (blue) 
  'C': '#f39c12', // Context (orange)
  'M': '#e74c3c'  // Mechanics (red)
};

// A combined map if your text may contain G/S even for English, etc.
// Feel free to adapt as needed.
const colorMapCombined = {
  ...colorMapEnglish,
  ...colorMapAiste,
  ...colorMapGCSE
};

// Add GCSE Language map
const colorMapGCSELang = {
  'C': '#f39c12', // Content
  'L': '#3498db', // Language
  'A': '#e74c3c'  // Accuracy
};

const colorMapCombinedWithGCSELang = {
  ...colorMapCombined,
  ...colorMapGCSELang
};

////////////////////////////////////////////////////////////////////////////////
// 2. parseForRevision: remove double-bracket blocks, split on single-bracket
////////////////////////////////////////////////////////////////////////////////
function parseForRevision(fullText) {
  // If you want to remove the entire "Total Score Calculation" block:
  const scoreIndex = fullText.indexOf('Total Score Calculation');
  if (scoreIndex !== -1) {
    fullText = fullText.slice(0, scoreIndex); 
  }

  // Now remove just the brackets but keep the words:
  let withoutDouble = fullText.replace(/\{\{([PACLMGSE]):\s*(.*?)\}\}/g, '$2');

  // Add a console log to see the text without brackets
  logger.debug("Text after removing double brackets:", withoutDouble.substring(0, 100) + "...");

  // Split on single-bracket comments {P: ...}, capturing them in the result array
  const splitRegex = /(\{[PACLMGSE]:.*?\})/g;
  const parts = withoutDouble.split(splitRegex);

  logger.debug("Parts split by single brackets:", parts.length);

  const segments = [];
  for (let i = 0; i < parts.length; i++) {
    const piece = parts[i];
    if (!piece) continue;

    const trimmed = piece.trim();
    // Is this piece a single-bracket comment block?
    if (/^\{[PACLMGSE]:.*?\}$/.test(trimmed)) {
      segments.push({
        type: 'comment',
        content: trimmed
      });
    } else {
      // It's essay text. Skip if it's too short (e.g. <6 chars) to avoid empty textareas.
      if (trimmed.length < 6) continue;

      segments.push({
        type: 'text',
        content: piece // Keep original whitespace
      });
    }
  }
  
  logger.debug("Parsed segments:", segments.length);
  return segments;
}

// First, add a helper function to check if it's a GCSE category
function isGCSECategory(category) {
  return category === 'GCSE_Shakespeare' || category === 'GCSE_19th' || category === 'GCSE_Poetry' || category === 'GCSE_Inspector';
}

// Helper function to check if it's an Oral category
function isOralCategory(category) {
  return category === 'IE_Oral' || category === 'FR_Oral' || category === 'ES_Oral' || category === 'DE_Oral';
}

// Helper function to check if it's an English category
function isEnglishCategory(category) {
  // Explicitly check for English categories
  return category === "Shakespeare" || 
         category === "Poetry" || 
         category === "Composition" || 
         category === "Comparative";
}

// Helper function to check if it's a GCSE Language category
function isGCSELanguageCategory(category) {
  return category === 'GCSE_French_Q1' || category === 'GCSE_French_Q2' || 
         category === 'GCSE_Spanish_Q1' || category === 'GCSE_Spanish_Q2' || 
         category === 'GCSE_German_Q1' || category === 'GCSE_German_Q2' ||
         category === 'GCSE_FR_Grammar' || category === 'GCSE_ES_Grammar' || category === 'GCSE_DE_Grammar';
}

export default function ResultAndRevision({
  // Props needed from MainContent
  question,
  result,
  usageRemaining,
  feedbackType,
  answer,
  setAnswer,
  handleResetApp,
  handleSavePDF,
  feedbackSubmitted,
  handleFeedbackSubmit,
  feedbackCategory,
  setFeedbackCategory,
  feedbackText,
  setFeedbackText,
  isRevising,
  setIsRevising,
  setStep,
  editedSections,
  setEditedSections,
  scrollToTop,
  category,
  taskId,
  authToken,
  API_BASE_URL,
  essayId,
  userData,
  initialTeacherFeedback,
  wordCount
}) {
  const [commentRatings, setCommentRatings] = useState({});
  const [error, setError] = useState(null);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [teacherFeedback, setTeacherFeedback] = useState(initialTeacherFeedback || '');
  const [teacherFeedbackInput, setTeacherFeedbackInput] = useState(initialTeacherFeedback || '');
  const [teacherFeedbackSubmitted, setTeacherFeedbackSubmitted] = useState(false);
  const [feedbackSaveStatus, setFeedbackSaveStatus] = useState(null);
  const [editedSegments, setEditedSegments] = useState([]);
  const [originalAnswer, setOriginalAnswer] = useState(answer);
  
  // Function to check if teacher exists for subject
  const hasTeacherForSubject = (subject) => {
    if (!userData?.teacher_email) return false;
    try {
      if (userData.teacher_email.startsWith('{')) {
        const teacherDict = JSON.parse(userData.teacher_email);
        return !!teacherDict[subject];
      }
      // Legacy format - only for English
      return subject === 'English' ? !!userData.teacher_email : false;
    } catch (e) {
      logger.error('Error parsing teacher email:', e);
      return false;
    }
  };

  // Function to determine subject from category
  const getCategorySubject = (category) => {
    if (!category) return 'Unknown';
    
    // Map category to subject with exact prefix matching
    if (category === 'Shakespeare' || category === 'Poetry' || 
        category === 'Comparative' || category === 'Composition' ||
        category.startsWith('GCSE_')) {
      return 'English';
    } else if (category === 'Aiste' || category === 'Grammar Checker' || 
              category === 'IE_Oral' || category.startsWith('Filiocht')) {
      return 'Irish';
    } else if (category.startsWith('FR_')) {
      return 'French';
    } else if (category.startsWith('ES_')) {
      return 'Spanish';
    } else if (category.startsWith('DE_')) {
      return 'German';
    } else if (category.startsWith('MATH_')) {
      return 'Maths';
    } else {
      // Log unknown categories for debugging
      logger.warn(`Unknown category found: "${category}", defaulting to Unknown`);
      return 'Unknown'; 
    }
  };

  // Function to check if warning about word count is needed
  const getWordCountWarning = () => {
    if (!isEnglishCategory(category) || !wordCount) return null;
    
    let targetWordCount = 0;
    
    // Set target word count based on category
    if (category === 'Poetry') {
      targetWordCount = 700;
    } else if (category === 'Shakespeare') {
      targetWordCount = 800;
    } else if (category === 'Comparative') {
      targetWordCount = 900;
    } else if (category === 'Composition') {
      targetWordCount = 1000;
    } else {
      return null; // No warning for other categories
    }
    
    // Only show warning if word count is below target
    if (wordCount < targetWordCount) {
      return (
        <div className="word-count-warning" style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '10px 15px',
          marginTop: '15px',
          marginBottom: '15px',
          borderRadius: '4px',
          borderLeft: '4px solid #ffc107'
        }}>
          <p style={{ margin: 0 }}>
            Note: your essay is {wordCount} words long. A {category} essay may be marked down if it is below {targetWordCount} words. 
            This is reflected in Pulc's grade below, but some examiners may penalise more or less than Pulc does.
          </p>
        </div>
      );
    }
    
    return null;
  };
  
  // State for editing feedback items
  const [editableFeedback, setEditableFeedback] = useState(null); // {paraIndex, fbIndex, content}
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [updatedFeedbackContent, setUpdatedFeedbackContent] = useState('');
  const [feedbackUpdateStatus, setFeedbackUpdateStatus] = useState(null);
  const [parsedFeedback, setParsedFeedback] = useState([]);
  
  // New state for adding feedback
  const [isAddingFeedback, setIsAddingFeedback] = useState(false);
  const [addFeedbackParaIndex, setAddFeedbackParaIndex] = useState(null);
  const [newFeedbackContent, setNewFeedbackContent] = useState('');
  const [newFeedbackType, setNewFeedbackType] = useState('P'); // Default to Purpose

  // Add state for editing grade section
  const [isEditingGrade, setIsEditingGrade] = useState(false);
  const [editedGradeText, setEditedGradeText] = useState('');
  const [gradeSaveStatus, setGradeSaveStatus] = useState(null);

  // Declare gradingText variable at the top level
  let gradingText = '';
  // Extract the grading text from the result if available
  if (result) {
    const idx = result.indexOf('Total Score Calculation');
    if (idx !== -1) {
      gradingText = result.slice(idx);
    }
  }
  
  // Add state for storing the latest grade text to ensure re-rendering
  const [displayedGradeText, setDisplayedGradeText] = useState(gradingText);
  
  // Add state for FAQ popup
  const [showFAQ, setShowFAQ] = useState(false);
  // Add state for tracking expanded FAQ questions
  const [expandedQuestions, setExpandedQuestions] = useState({
    reliability: true,
    lowGrade: false,
    highGrade: false,
    different: false,
    worse: false,
    wrongMarks: false
  });
  
  // Helper function to toggle individual FAQ questions
  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
    
    track('faq_question_toggled', { 
      category: category,
      questionId: questionId,
      action: !expandedQuestions[questionId] ? 'expanded' : 'collapsed'
    });
  };

  // Add refs for textareas
  const textareaRefs = useRef({});

  // Improved textarea rendering with better ref handling
  const renderRevisionContent = (content) => {
    const segments = editedSegments.length > 0 ? editedSegments : parseForRevision(content);
    
    logger.debug(`Rendering ${segments.length} segments in revision mode`);
    logger.debug("textareaRefs.current keys:", Object.keys(textareaRefs.current));
    
    return segments.map((segment, index) => {
      if (segment.type === 'text') {
        logger.debug(`Rendering textarea ${index}: ${segment.content.substring(0, 30)}...`);
        
        return (
          <div key={index} className="revise-textarea-container">
            <textarea
              key={`textarea-${index}`}
              ref={el => {
                if (el) {
                  logger.debug(`Setting ref for textarea-${index}`);
                  textareaRefs.current[`textarea-${index}`] = el;
                }
              }}
              defaultValue={segment.content}
              className="revise-textarea"
              id={`revise-textarea-${index}`}
            />
          </div>
        );
      } else if (segment.type === 'comment') {
        // Extract the letter + comment text
        // e.g. {P: some text} => code = "P", text = "some text"
        let code = 'P';
        let commentBody = segment.content;
        const match = segment.content.match(/^\{([PACLMGSE]):\s*(.*?)\}$/);
        if (match) {
          code = match[1];
          commentBody = match[2];
        }

        // Pick color from category or fallback to combined map
        const colorMap = (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral') 
          ? colorMapAiste 
          : isGCSECategory(category) // English GCSE
            ? colorMapGCSE
            : isGCSELanguageCategory(category) // French/Spanish/German GCSE
              ? colorMapGCSELang
            : colorMapEnglish;
        
        // If code is not in the map, fallback to combined or pick a default
        const color = colorMap[code] || colorMapCombinedWithGCSELang[code] || '#3498db';
        
        // Render the comment with the appropriate color
        return (
          <div
            key={index}
            style={{
              margin: '10px 0',
              padding: '10px',
              backgroundColor: color + '22',
              borderLeft: `4px solid ${color}`,
              borderRadius: '4px'
            }}
            dangerouslySetInnerHTML={{ __html: commentBody }}
          />
        );
      }
      return null;
    });
  };

  // Improved function to collect values from all textareas
  const collectTextareaValues = () => {
    logger.debug("Collecting values from all textareas...");
    
    // Get all textarea elements by class name - most reliable approach
    const allTextareas = document.querySelectorAll('.revise-textarea');
    logger.debug(`Found ${allTextareas.length} textarea elements in DOM by class`);
    
    // Also try getting by ID for verification
    const textareasByID = Array.from({ length: 20 }, (_, i) => 
      document.getElementById(`revise-textarea-${i}`)
    ).filter(Boolean);
    logger.debug(`Found ${textareasByID.length} textarea elements in DOM by ID`);
    
    // Get values from DOM elements directly - most reliable method
    const valuesFromDOM = Array.from(allTextareas).map((textarea, idx) => {
      const value = textarea.value || '';
      logger.debug(`[DOM] Textarea ${idx} value (${value.length} chars):`, value.substring(0, 30) + (value.length > 30 ? "..." : ""));
      return value;
    });
    
    logger.debug(`Collected ${valuesFromDOM.length} values from DOM`);
    logger.debug(`Total text length: ${valuesFromDOM.join('').length} characters`);
    
    return valuesFromDOM.join('');
  };

  // Make sure the useEffect properly initializes the refs
  useEffect(() => {
    if (isRevising) {
      // If there are existing textareas, get their current values
      const existingTextareas = document.querySelectorAll('.revise-textarea');
      let currentValues = {};
      
      if (existingTextareas.length > 0) {
        logger.debug(`Found ${existingTextareas.length} existing textareas, capturing their values`);
        
        Array.from(existingTextareas).forEach((textarea, idx) => {
          const value = textarea.value || '';
          currentValues[idx] = value;
          logger.debug(`Captured existing textarea ${idx} value: ${value.substring(0, 30)}...`);
        });
      }
      
      // Completely clear the refs object first
      logger.debug("Resetting textareaRefs object for new revision");
      textareaRefs.current = {};
      
      // Parse the text segments when revision mode is started
      const segments = parseForRevision(result);
      logger.debug(`Initialized revision mode with ${segments.length} segments`);
      
      // Update segments with any existing values
      if (Object.keys(currentValues).length > 0) {
        for (let i = 0; i < segments.length; i++) {
          if (segments[i].type === 'text' && currentValues[i]) {
            logger.debug(`Restoring textarea ${i} value from existing textarea`);
            segments[i].content = currentValues[i];
          }
        }
      }
      
      // Set initial editedSegments state
      setEditedSegments(segments);
      
      // Remember original answer for comparison
      setOriginalAnswer(result);
    }
  }, [isRevising, result]);

  // Add useEffect to initialize grade text for editing
  useEffect(() => {
    if (gradingText) {
      // Save the full text including any [TEACHER] tag for display
      setDisplayedGradeText(gradingText);
      
      // Remove [TEACHER] tag for the editable version
      if (gradingText.includes('[TEACHER]')) {
        const cleanedText = gradingText.replace(/\s*\[TEACHER\]\s*/, '');
        setEditedGradeText(cleanedText);
      } else {
        setEditedGradeText(gradingText);
      }
    }
  }, [gradingText]);

  // Add useEffect to parse feedback when result changes
  useEffect(() => {
    if (result && feedbackType === 'inline') {
      let trimmedContent = result;
      const idx = result.indexOf('Total Score Calculation');
      if (idx !== -1) {
        trimmedContent = result.slice(0, idx);
      }
      const parsed = parseLineByLine(trimmedContent, category);
      setParsedFeedback(parsed);
    }
  }, [result, category, feedbackType]);

  // Add useEffect to monitor state
  useEffect(() => {
    logger.info('Initial teacher feedback:', initialTeacherFeedback);
    logger.info('Teacher feedback input:', teacherFeedbackInput);
  }, [initialTeacherFeedback, teacherFeedbackInput]);

  // Handle editing the grade section
  const handleEditGrade = () => {
    if (userData?.role !== 'teacher') return;
    
    track('grade_edit_started', { 
      category: category,
      isTeacher: true
    });
    
    // Remove [TEACHER] tag when entering edit mode
    let textToEdit = editedGradeText;
    if (textToEdit.includes('[TEACHER]')) {
      textToEdit = textToEdit.replace(/\s*\[TEACHER\]\s*/, '');
      setEditedGradeText(textToEdit);
    }
    
    setIsEditingGrade(true);
  };

  // Handle saving the edited grade
  const handleSaveGrade = async () => {
    if (userData?.role !== 'teacher' || !taskId) return;
    
    try {
      setGradeSaveStatus('saving');
      
      track('grade_edited', { 
        category: category,
        isTeacher: true,
        essayId: essayId
      });
      
      // Ensure the [TEACHER] tag is included but not duplicated
      let gradeToSave = editedGradeText;
      if (!gradeToSave.includes('[TEACHER]')) {
        gradeToSave = gradeToSave.trim() + ' [TEACHER]';
      }
      
      // Call API to update the grade in the database
      const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_grade`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grade_text: gradeToSave
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save grade');
      }
      
      // Update the grade text and state to reflect changes immediately
      gradingText = gradeToSave;
      setDisplayedGradeText(gradeToSave);
      
      // Reset the editing state
      setIsEditingGrade(false);
      setGradeSaveStatus('success');
      setTimeout(() => setGradeSaveStatus(null), 2000);
    } catch (error) {
      logger.error('Error saving edited grade:', error);
      setGradeSaveStatus('error');
      alert(`Error saving grade: ${error.message}`);
    }
  };

  // Handle canceling the grade edit
  const handleCancelGradeEdit = () => {
    track('grade_edit_cancelled', { 
      category: category,
      isTeacher: true
    });
    
    setIsEditingGrade(false);
    setEditedGradeText(displayedGradeText); // Reset to current displayed text
  };

  // Handle editing a feedback item
  const handleEditFeedback = (paraIndex, fbIndex, feedbackContent) => {
    // Only allow teachers to edit feedback
    if (userData?.role !== 'teacher') return;
    
    track('feedback_edit_started', { 
      category: category,
      isTeacher: true
    });
    
    // Store more detailed information to help with recovery
    const editInfo = { 
      paraIndex, 
      fbIndex,
      content: feedbackContent,
      contentKey: feedbackContent.substring(0, 50),
      // Extract the feedback type (P, C, L, M, G, S, E)
      type: (feedbackContent.match(/^\{([PACLMGSE]):/) || [null, 'P'])[1]
    };
    
    setEditableFeedback(editInfo);
    setIsEditingFeedback(true);
    
    // Clean the feedback content for editing (remove any brackets and TEACHER tag)
    const cleanedContent = feedbackContent
      .replace(/^\{[PACLMGSE]:/, '') // Remove the opening tag and type
      .replace(/\}(\s*\[TEACHER\])?$/, '') // Remove closing bracket and [TEACHER] tag
      .trim();
      
    logger.info('Preparing feedback for editing', {
      original: feedbackContent,
      cleaned: cleanedContent,
      editInfo
    });
    
    // Store this information in localStorage as a backup
    try {
      localStorage.setItem('currentEditingFeedback', JSON.stringify(editInfo));
    } catch (e) {
      logger.error('Failed to store editing info in localStorage:', e);
    }
    
    setUpdatedFeedbackContent(cleanedContent);
  };

  // Handle deleting a feedback item
  const handleDeleteFeedback = async (paraIndex, fbIndex) => {
    // Only allow teachers to delete feedback
    if (userData?.role !== 'teacher' || !taskId) return;
    
    if (window.confirm('Are you sure you want to delete this feedback item?')) {
      try {
        setFeedbackUpdateStatus('saving');
        
        track('feedback_deleted', { 
          category: category,
          isTeacher: true,
          feedbackIndex: `${paraIndex}-${fbIndex}`
        });
        
        // Log starting state for debugging
        logger.debug(`BEFORE DELETION - Current ratings:`, JSON.stringify(commentRatings));
        logger.debug(`Deleting comment at paragraph ${paraIndex}, feedback index ${fbIndex}`);
        
        // Call API to update the feedback in the database
        const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_feedback`, {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            operation: 'delete',
            feedbackKey: `${paraIndex}-${fbIndex}`,
            // Send the actual feedback content to help the server identify it
            feedbackContent: parsedFeedback[paraIndex]?.feedback[fbIndex]
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            errorData = { error: errorText };
          }
          logger.error('Error response from delete feedback API', {
            status: response.status,
            errorData
          });
          throw new Error(errorData.error || 'Failed to delete feedback');
        }

        // Update the UI to remove the deleted feedback
        const newParsedFeedback = JSON.parse(JSON.stringify(parsedFeedback));
        newParsedFeedback[paraIndex].feedback.splice(fbIndex, 1);
        setParsedFeedback(newParsedFeedback);
        
        // Log the new feedback array with indices AFTER deletion for debugging
        logger.debug("FEEDBACK AFTER DELETION - UPDATED INDICES:");
        newParsedFeedback[paraIndex].feedback.forEach((item, idx) => {
          const originalIdx = idx >= fbIndex ? idx + 1 : idx;
          logger.debug(`  New index ${paraIndex}-${idx} was originally ${paraIndex}-${originalIdx}: ${item.substring(0, 30)}...`);
        });
        
        // Store the updated feedback content BEFORE ratings remapping
        // This will help us verify the item exists before attempting edit operations
        // Create a lookup map of feedback content to indices
        const feedbackContentToIndexMap = {};
        newParsedFeedback.forEach((paragraph, pIdx) => {
          paragraph.feedback.forEach((content, fIdx) => {
            // Create a unique key for this feedback item's content (first 50 chars)
            const contentKey = content.substring(0, 50);
            feedbackContentToIndexMap[contentKey] = `${pIdx}-${fIdx}`;
          });
        });
        
        // Store this map in localStorage for persistence
        try {
          localStorage.setItem('feedbackContentToIndexMap', JSON.stringify(feedbackContentToIndexMap));
          logger.debug("Saved feedback content-to-index map to localStorage");
        } catch (e) {
          logger.error("Failed to save feedback map to localStorage:", e);
        }
        
        // Completely rewritten rating remapping logic with better logging
        // -------------------------------------------------------------
        
        // Create direct mapping between comment content and their original ratings
        logger.debug(`Creating content-to-rating map for paragraph ${paraIndex}`);
        
        // Step 1: Make a copy of the original feedback array BEFORE deletion
        const originalFeedback = [...parsedFeedback[paraIndex].feedback];
        
        logger.debug(`Original feedback before deletion (${originalFeedback.length} items):`, 
          originalFeedback.map((f, i) => `${i}: ${f.substring(0, 30)}...`));
        
        // Step 2: Create a map from feedback content to rating value
        const feedbackContentToRating = {};
        const feedbackIndexToRating = {}; // Backup mapping by index
        let hasRatings = false;
        
        originalFeedback.forEach((content, idx) => {
          const ratingKey = `${paraIndex}-${idx}`;
          // Only store feedbacks that actually have ratings
          if (commentRatings[ratingKey]) {
            hasRatings = true;
            // Create a unique hash/key for the content to avoid collision
            // Using first 50 chars should be unique enough for most comments
            const contentKey = content.substring(0, 50);
            feedbackContentToRating[contentKey] = commentRatings[ratingKey];
            feedbackIndexToRating[idx] = commentRatings[ratingKey]; // Store by index as backup
            logger.debug(`Feedback ${idx} "${contentKey}..." had rating: ${commentRatings[ratingKey]}`);
          }
        });
        
        if (!hasRatings) {
          logger.debug(`No ratings found for any feedback in paragraph ${paraIndex}, nothing to remap.`);
        }
        
        // Step 3: Create new ratings object, first copying ratings from other paragraphs
        const newRatings = {};
        Object.entries(commentRatings).forEach(([key, value]) => {
          if (!key.startsWith(`${paraIndex}-`)) {
            newRatings[key] = value;
          }
        });
        
        // Step 4: Map the NEW feedback comments to their original ratings based on content
        const newFeedback = newParsedFeedback[paraIndex].feedback;
        logger.debug(`New feedback after deletion (${newFeedback.length} items):`, 
          newFeedback.map((f, i) => `${i}: ${f.substring(0, 30)}...`));
          
        // Primary approach: content-based matching
        let contentMatchesFound = 0;
        newFeedback.forEach((content, newIdx) => {
          const contentKey = content.substring(0, 50);
          if (feedbackContentToRating[contentKey]) {
            newRatings[`${paraIndex}-${newIdx}`] = feedbackContentToRating[contentKey];
            contentMatchesFound++;
            logger.debug(`Content match: Setting rating for position ${newIdx} based on content match: ${feedbackContentToRating[contentKey]}`);
          }
        });
        
        // If content-based matching failed completely, try index-based as fallback
        // This helps in rare cases where content changes make exact matching impossible
        if (hasRatings && contentMatchesFound === 0) {
          logger.debug(`Warning: No content matches found! Trying position-based mapping as fallback.`);
          
          // Skip the deleted index for all subsequent ratings
          let oldIndex = 0;
          for (let newIdx = 0; newIdx < newFeedback.length; newIdx++) {
            // If this was the deleted index, skip it
            if (oldIndex === parseInt(fbIndex)) {
              oldIndex++;
            }
            
            // If this old index had a rating, map it to the new index
            if (feedbackIndexToRating[oldIndex]) {
              newRatings[`${paraIndex}-${newIdx}`] = feedbackIndexToRating[oldIndex];
              logger.debug(`Fallback: Mapping rating from old index ${oldIndex} to new index ${newIdx}: ${feedbackIndexToRating[oldIndex]}`);
            }
            
            oldIndex++;
          }
        }
        
        // Important: We DO NOT create ratings for comments that didn't have them before!
        
        logger.debug(`AFTER REMAPPING - New ratings object:`, JSON.stringify(newRatings));
        
        // Update the ratings state
        setCommentRatings(newRatings);
        
        // Save the updated ratings to the server
        try {
          const ratingsResponse = await fetch(
            `${API_BASE_URL}/api/results/${taskId}/update_ratings`,
            {
              method: 'POST',
              headers: {
                'Authorization': authToken,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                commentRatings: newRatings,
                replace_all: true // Set to true to completely replace ratings instead of merging
              })
            }
          );
          
          if (!ratingsResponse.ok) {
            logger.error('Failed to update ratings after comment deletion', {
              status: ratingsResponse.status
            });
          } else {
            logger.debug('Successfully saved remapped ratings to server');
          }
        } catch (ratingError) {
          logger.error('Error updating ratings after deletion:', ratingError);
          // Don't throw here - we still want to show success for the main operation
        }
                
        setFeedbackUpdateStatus('success');
        setTimeout(() => setFeedbackUpdateStatus(null), 2000);
      } catch (error) {
        logger.error('Error deleting feedback:', error);
        setFeedbackUpdateStatus('error');
        alert(`Error deleting feedback: ${error.message}`);
      }
    }
  };

  // Handle saving the edited feedback
  const handleSaveEditedFeedback = async () => {
    if (!editableFeedback || userData?.role !== 'teacher' || !taskId) return;
    
    try {
      setFeedbackUpdateStatus('saving');
      
      // Get the feedback type code from the original feedback
      const { paraIndex, fbIndex } = editableFeedback;
      
      // Check if the feedback item still exists at this index
      if (!parsedFeedback[paraIndex] || !parsedFeedback[paraIndex].feedback[fbIndex]) {
        logger.error('Feedback item no longer exists at specified indices', { paraIndex, fbIndex });
        
        // Try to recover by looking for similar content in our index map
        let foundItem = false;
        let correctParaIndex = paraIndex;
        let correctFbIndex = fbIndex;
        
        try {
          // Get the stored content-to-index map
          const storedMap = localStorage.getItem('feedbackContentToIndexMap');
          if (storedMap) {
            const contentToIndexMap = JSON.parse(storedMap);
            
            // Get the original content (first few characters) for this feedback item
            const originalFeedbackStart = parsedFeedback[paraIndex]?.feedback[fbIndex]?.substring(0, 50);
            
            if (originalFeedbackStart) {
              // Search for this content in the map
              // First look for exact match
              Object.entries(contentToIndexMap).forEach(([content, idx]) => {
                if (content.includes(originalFeedbackStart) || originalFeedbackStart.includes(content)) {
                  logger.info('Found matching content in index map', { content, idx });
                  const [pIdx, fIdx] = idx.split('-').map(Number);
                  correctParaIndex = pIdx;
                  correctFbIndex = fIdx;
                  foundItem = true;
                }
              });
            }
          }
        } catch (e) {
          logger.error('Error trying to recover feedback indices from stored map', e);
        }
        
        if (!foundItem) {
          throw new Error('Feedback item not found. It may have been deleted or moved.');
        }
        
        logger.info('Recovered correct indices for feedback item', { 
          original: `${paraIndex}-${fbIndex}`, 
          corrected: `${correctParaIndex}-${correctFbIndex}` 
        });
        
        // Update indices to use for the edit
        editableFeedback.paraIndex = correctParaIndex;
        editableFeedback.fbIndex = correctFbIndex;
      }
      
      // Get the updated indices
      const { paraIndex: updatedParaIndex, fbIndex: updatedFbIndex } = editableFeedback;
      
      // Get the original feedback with the updated indices
      const originalFeedback = parsedFeedback[updatedParaIndex].feedback[updatedFbIndex];
      
      if (!originalFeedback) {
        throw new Error('Feedback item not found even after index correction. Operation aborted.');
      }
      
      // Extract the code (P, C, L, M, G, S, etc.) from the original feedback
      const codeMatch = originalFeedback.match(/^\{([PACLMGSE]):/);
      const feedbackCode = codeMatch ? codeMatch[1] : 'P'; // Default to P if not found
      
      // Format the new feedback with the same code and mark as teacher-edited
      const formattedFeedback = `{${feedbackCode}: ${updatedFeedbackContent}} [TEACHER]`;
      
      track('feedback_edited', { 
        category: category,
        isTeacher: true,
        feedbackIndex: `${updatedParaIndex}-${updatedFbIndex}`,
        feedbackType: feedbackCode
      });
      
      logger.info('Saving edited feedback', {
        original: originalFeedback,
        updated: formattedFeedback,
        paraIndex: updatedParaIndex,
        fbIndex: updatedFbIndex
      });
      
      // Call API to update the feedback in the database
      const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_feedback`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          operation: 'update',
          feedbackKey: `${updatedParaIndex}-${updatedFbIndex}`,
          content: formattedFeedback,
          feedbackContent: originalFeedback  // Send original content to help with matching
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to save feedback');
      }
      
      // Update the UI with the edited feedback
      const newParsedFeedback = JSON.parse(JSON.stringify(parsedFeedback));
      newParsedFeedback[updatedParaIndex].feedback[updatedFbIndex] = formattedFeedback;
      setParsedFeedback(newParsedFeedback);
      
      // Update the content-to-index map with the new content
      try {
        const storedMap = localStorage.getItem('feedbackContentToIndexMap');
        if (storedMap) {
          const contentToIndexMap = JSON.parse(storedMap);
          // Remove any entries that point to this index 
          Object.keys(contentToIndexMap).forEach(key => {
            if (contentToIndexMap[key] === `${updatedParaIndex}-${updatedFbIndex}`) {
              delete contentToIndexMap[key];
            }
          });
          // Add the new content mapping
          contentToIndexMap[formattedFeedback.substring(0, 50)] = `${updatedParaIndex}-${updatedFbIndex}`;
          localStorage.setItem('feedbackContentToIndexMap', JSON.stringify(contentToIndexMap));
          logger.debug("Updated feedback content-to-index map in localStorage");
        }
      } catch (e) {
        logger.error("Failed to update feedback map in localStorage:", e);
      }
      
      // Reset the editing state
      setIsEditingFeedback(false);
      setEditableFeedback(null);
      setUpdatedFeedbackContent('');
      setFeedbackUpdateStatus('success');
      setTimeout(() => setFeedbackUpdateStatus(null), 2000);
    } catch (error) {
      logger.error('Error saving edited feedback:', error);
      setFeedbackUpdateStatus('error');
      alert(`Error saving feedback: ${error.message}`);
    }
  };

  // Handle canceling the feedback edit
  const handleCancelEdit = () => {
    track('feedback_edit_cancelled', { 
      category: category,
      isTeacher: true
    });
    
    setIsEditingFeedback(false);
    setEditableFeedback(null);
    setUpdatedFeedbackContent('');
  };

  const handleSaveTeacherFeedback = async () => {
    try {
      // Validate input before sending
      if (!teacherFeedbackInput || teacherFeedbackInput.trim() === '') {
        setFeedbackSaveStatus('error');
        alert('Please enter some feedback before saving.');
        return;
      }

      setFeedbackSaveStatus('saving');
      logger.info('Saving teacher feedback:', teacherFeedbackInput);
      
      track('teacher_feedback_submitted', { 
        category: category,
        isTeacher: true,
        essayId: essayId || taskId,
        characterCount: teacherFeedbackInput.length
      });
      
      // For a teacher's own essay, we might not have essayId but we will have taskId
      // If essayId is missing but taskId is available, use a different endpoint
      
      let endpoint;
      let requestBody;
      
      if (essayId) {
        // Standard endpoint for student essays shared with a teacher
        endpoint = `${API_BASE_URL}/api/teacher-feedback/${essayId}`;
        requestBody = {
          teacher_feedback: teacherFeedbackInput
        };
      } else if (taskId) {
        // Alternative endpoint for a teacher's own essays
        endpoint = `${API_BASE_URL}/api/results/${taskId}/update_teacher_feedback`;
        requestBody = {
          teacher_feedback: teacherFeedbackInput
        };
      } else {
        throw new Error('No essay ID or task ID available, cannot save feedback');
      }
      
      logger.info(`Using endpoint ${endpoint} to save teacher feedback`);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') 
            ? authToken 
            : `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save feedback');
      }

      const data = await response.json();
      setTeacherFeedback(teacherFeedbackInput);
      setTeacherFeedbackSubmitted(true);
      setFeedbackSaveStatus('success');
      
      // Show success message with instructions
      alert(
        'Feedback saved successfully!\n\n' 
      );

    } catch (error) {
      logger.error('Error saving teacher feedback:', error);
      setFeedbackSaveStatus('error');
      alert(
        'Error saving feedback: ' + error.message + '\n\n' +
        'Please copy your feedback to a safe place and try again. ' +
        'If the problem persists, please contact support at hello@pulc.ie'
      );
    }
  };

  // Load existing ratings when component mounts
  useEffect(() => {
    const loadExistingRatings = async () => {
      if (!taskId) return;
      
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/results/${taskId}/ratings`,
          {
            headers: {
              'Authorization': authToken
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.ratings) {
            setCommentRatings(data.ratings);
          }
        }
      } catch (error) {
        logger.error('Error loading ratings:', error);
      }
    };
    
    loadExistingRatings();
  }, [taskId, authToken, API_BASE_URL]);

  // Add useEffect to log when shortTaskId changes
  useEffect(() => {
    logger.info('ResultAndRevision received shortTaskId:', taskId);
  }, [taskId]);

  // Define handleCommentRating inside the component
  const handleCommentRating = async (paraIndex, fbIndex, rating) => {
    if (!taskId) {
      logger.error('Cannot rate yet - waiting for task ID');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/results/${taskId}/update_ratings`,
        {
          method: 'POST',
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            commentRatings: {
              [`${paraIndex}-${fbIndex}`]: rating
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update rating');
      }

      // Handle success
      logger.info('Rating updated successfully');
      // Update local state to show the selected rating
      setCommentRatings(prev => ({
        ...prev,
        [`${paraIndex}-${fbIndex}`]: rating
      }));

    } catch (error) {
      logger.error('Rating error:', error);
      // Could add user-facing error message here
    }
  };
  
  // Early return after useState declaration
  if (!result) return null;

  let trimmedResult = result;
  const idx = result.indexOf('Total Score Calculation');
  if (idx !== -1) {
    trimmedResult = result.slice(0, idx);
  }

  const handleDownloadOriginal = () => {
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Original Submitted Answer", 10, 10);
    const questionLines = doc.splitTextToSize(`Question: ${question}`, 180);
    let y = 20;
    const pageHeight = doc.internal.pageSize.getHeight();
    for (let i = 0; i < questionLines.length; i++) {
      if (y + 10 > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }
      doc.text(questionLines[i], 10, y);
      y += 10;
    }
    y += 10;
    const answerLines = doc.splitTextToSize(answer, 180);
    for (let i = 0; i < answerLines.length; i++) {
      if (y + 10 > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }
      doc.text(answerLines[i], 10, y);
      y += 10;
    }
    doc.save("original_answer.pdf");
  };

  // Add tracking for key user interactions
  const handleDownloadWithTracking = () => {
    track('pdf_downloaded', { category: category });
    handleSavePDF();
  };

  const handleDownloadOriginalWithTracking = () => {
    track('original_answer_downloaded', { category: category });
    handleDownloadOriginal();
  };

  const handleStartRevisionWithTracking = () => {
    track('revision_started', { category: category });
    setIsRevising(true);
    scrollToTop();
  };

  const handleSubmitFeedbackWithTracking = (e) => {
    track('user_feedback_submitted', { 
      category: category,
      feedbackCategory: feedbackCategory 
    });
    handleFeedbackSubmit(e);
  };

  // Add tracking to comment ratings
  const handleCommentRatingWithTracking = async (paraIndex, fbIndex, rating) => {
    track('comment_rated', { 
      category: category,
      rating: rating 
    });
    await handleCommentRating(paraIndex, fbIndex, rating);
  };

  // Handle adding a new feedback item
  const handleAddFeedback = (paraIndex) => {
    track('feedback_add_started', { 
      category: category,
      isTeacher: true,
      paragraphIndex: paraIndex
    });
    
    setIsAddingFeedback(true);
    setAddFeedbackParaIndex(paraIndex);
    setNewFeedbackContent('');
    
    // Set default feedback type based on category
    if (isGCSECategory(category)) {
      setNewFeedbackType('A'); // Argument
    } else if (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral') {
      setNewFeedbackType('G'); // Gaeilge
    } else if (category.startsWith('FR_') || category.startsWith('ES_')) {
      setNewFeedbackType('C'); // Communication
    } else if (category.startsWith('DE_')) {
      setNewFeedbackType('C'); // Content
    } else {
      setNewFeedbackType('P'); // Purpose
    }
  };

  // Handle saving the new feedback
  const handleSaveNewFeedback = async () => {
    if (addFeedbackParaIndex === null || !newFeedbackContent.trim() || userData?.role !== 'teacher' || !taskId) {
      logger.warn('Cannot add feedback - missing required data', {
        addFeedbackParaIndex,
        contentLength: newFeedbackContent?.trim()?.length,
        isTeacher: userData?.role === 'teacher',
        hasTaskId: !!taskId
      });
      return;
    }
    
    try {
      setFeedbackUpdateStatus('saving');
      
      // Format the new feedback with the selected type and mark as teacher-added
      const formattedFeedback = `{${newFeedbackType}: ${newFeedbackContent.trim()}} [TEACHER]`;
      
      track('feedback_added', { 
        category: category,
        isTeacher: true,
        paragraphIndex: addFeedbackParaIndex,
        feedbackType: newFeedbackType,
        characterCount: newFeedbackContent.trim().length
      });
      
      // Create a new index for the added feedback at the end of the current array
      const newFbIndex = parsedFeedback[addFeedbackParaIndex].feedback.length;
      
      const requestBody = {
        operation: 'add',
        feedbackKey: `${addFeedbackParaIndex}-${newFbIndex}`,
        paraIndex: addFeedbackParaIndex,
        content: formattedFeedback
      };
      
      logger.info('Sending add feedback request', { 
        url: `${API_BASE_URL}/api/results/${taskId}/update_feedback`,
        body: requestBody 
      });
      
      // Call API to add the new feedback
      const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_feedback`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        logger.error('Error response from add feedback API', {
          status: response.status,
          errorData
        });
        throw new Error(errorData.error || `Failed to add feedback: ${response.status} ${response.statusText}`);
      }
      
      // Update the UI with the new feedback
      const newParsedFeedback = JSON.parse(JSON.stringify(parsedFeedback));
      newParsedFeedback[addFeedbackParaIndex].feedback.push(formattedFeedback);
      setParsedFeedback(newParsedFeedback);
      
      // Reset the adding state
      setIsAddingFeedback(false);
      setAddFeedbackParaIndex(null);
      setNewFeedbackContent('');
      setFeedbackUpdateStatus('success');
      setTimeout(() => setFeedbackUpdateStatus(null), 2000);
    } catch (error) {
      logger.error('Error adding new feedback:', error);
      setFeedbackUpdateStatus('error');
      alert(`Error adding feedback: ${error.message}`);
    }
  };

  // Handle canceling adding feedback
  const handleCancelAddFeedback = () => {
    track('feedback_add_cancelled', { 
      category: category,
      isTeacher: true
    });
    
    setIsAddingFeedback(false);
    setAddFeedbackParaIndex(null);
    setNewFeedbackContent('');
  };

  // Get appropriate feedback type options based on category
  const getFeedbackTypeOptions = () => {
    if (isGCSECategory(category)) {
      return [
        { value: 'A', label: 'Argument (A)', color: '#9b59b6' },
        { value: 'L', label: 'Language (L)', color: '#3498db' },
        { value: 'C', label: 'Context (C)', color: '#f39c12' },
        { value: 'M', label: 'Mechanics (M)', color: '#e74c3c' }
      ];
    } else if (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral') {
      return [
        { value: 'G', label: 'Gaeilge (G)', color: '#16a085' },
        { value: 'C', label: 'Content (C)', color: '#c0392b' },
        { value: 'S', label: 'Style (S)', color: '#8e44ad' }
      ];
    } else if (category.startsWith('FR_') || category.startsWith('ES_')) {
      return [
        { value: 'C', label: 'Communication (C)', color: '#FF6B6B' },
        { value: 'L', label: 'Language (L)', color: '#4ECDC4' }
      ];
    } else if (category.startsWith('DE_')) {
      return [
        { value: 'C', label: 'Content (C)', color: '#2ecc71' },
        { value: 'E', label: 'Expression (E)', color: '#3498db' }
      ];
    } else if (isGCSELanguageCategory(category)) {
      return [
        { value: 'C', label: 'Content (C)', color: '#f39c12' },
        { value: 'L', label: 'Language (L)', color: '#3498db' },
        { value: 'A', label: 'Accuracy (A)', color: '#e74c3c' }
      ];
    } else {
      return [
        { value: 'P', label: 'Purpose (P)', color: '#2ecc71' },
        { value: 'C', label: 'Coherence (C)', color: '#e74c3c' },
        { value: 'L', label: 'Language (L)', color: '#3498db' },
        { value: 'M', label: 'Mechanics (M)', color: '#f1c40f' }
      ];
    }
  };

  // Helper function to toggle FAQ visibility
  const toggleFAQ = () => {
    setShowFAQ(!showFAQ);
    track('faq_toggled', { 
      category: category,
      action: !showFAQ ? 'opened' : 'closed'
    });
  };

  // Add a function to process grade text and make headers bold
  const formatGradeText = (text) => {
    if (!text) return '';
    
    // Check if this is a teacher-edited grade
    const isTeacherEdited = text.includes("[TEACHER]");
    
    // Remove the [TEACHER] marker for display but keep track of it
    let displayText = text;
    if (isTeacherEdited) {
      displayText = text.replace(/\s*\[TEACHER\]\s*/, '');
    }
    
    // Replace the main headers with bold/styled versions
    let formattedText = displayText
      // Main "Total Score Calculation" header - make it the most prominent
      .replace(/Total\s+Score\s+Calculation:?/g, '<span class="grade-header-main">Total Score Calculation</span>')
      // Other section headers
      .replace(/Purpose\s*:?/g, '<span class="grade-header">Purpose:</span>')
      .replace(/Coherence\s*:?/g, '<span class="grade-header">Coherence:</span>')
      .replace(/Language\s+Use\s*:?/g, '<span class="grade-header">Language Use:</span>')
      .replace(/Accuracy\s+of\s+Mechanics\s*:?/g, '<span class="grade-header">Accuracy of Mechanics:</span>')
      .replace(/Final\s+Mark\s*:?/g, '<span class="grade-header-final">Final Mark:</span>')
      .replace(/Overall\s+Score\s*:?/g, '<span class="grade-header-final">Overall Score:</span>')
      .replace(/Final\s+Grade\s*:?/g, '<span class="grade-header-final">Final Grade:</span>')
      // Replace newlines with <br> tags
      .replace(/\n/g, '<br />');
    
    // If teacher-edited, wrap the entire content in a special container
    if (isTeacherEdited) {
      formattedText = `
        <div class="teacher-edited-grade">
          <span class="teacher-grade-badge">Teacher Edited</span>
          ${formattedText}
        </div>
      `;
    }
    
    return formattedText;
  };

  // Add a function to handle textarea changes directly
  const handleTextareaChange = (index, value) => {
    logger.debug(`Textarea ${index} changed: ${value.substring(0, 20)}...`);
    
    // If editedSegments is empty, initialize it with the parseForRevision result
    if (editedSegments.length === 0) {
      const initialSegments = parseForRevision(result);
      setEditedSegments(initialSegments);
      
      // Update the segment at index immediately
      initialSegments[index] = {
        ...initialSegments[index],
        content: value
      };
      
      // Build updated text from all segments
      const initialUpdatedText = initialSegments
        .filter(s => s.type === 'text')
        .map(s => s.content)
        .join('');
      
      setAnswer(initialUpdatedText);
      logger.debug(`Initialized and updated answer: ${initialUpdatedText.substring(0, 50)}...`);
      return;
    }
    
    // Make a deep copy of all edited segments
    const newSegments = JSON.parse(JSON.stringify(editedSegments));
    
    // Update only the segment that changed
    if (index < newSegments.length) {
      newSegments[index] = {
        ...newSegments[index],
        content: value
      };
      
      // Important: Store ALL the updated segments
      setEditedSegments(newSegments);
      
      // Rebuild the full text from ALL edited segments
      const updatedText = newSegments
        .filter(s => s.type === 'text')
        .map(s => s.content)
        .join('');
      
      // Update the answer state which will be used on "Confirm Revision"
      setAnswer(updatedText);
      logger.debug(`Updated segment ${index}, total segments: ${newSegments.length}`);
      logger.debug(`Updated answer: ${updatedText.substring(0, 50)}...`);
    } else {
      logger.error(`Invalid segment index: ${index}, segments length: ${newSegments.length}`);
    }
  };

  const handleConfirmClick = () => {
    // Get the current values directly from textareas
    logger.debug("Confirming revision with direct textarea value collection");
    const finalText = collectTextareaValues();
    
    // Update the global answer state
    setAnswer(finalText);
    logger.debug(`Setting answer with final text (${finalText.length} chars)`);
    
    // Reset the editedSegments state
    setEditedSegments([]);
    
    // Change back to standard result view
    setIsRevising(false);
    
    // Track analytics
    track('revision_confirmed', { 
      category: category,
      characterCount: finalText.length,
      edited: finalText !== originalAnswer
    });
    
    // Set the step to 4 (as it was in the original onClick handler)
    setStep(4);
    
    // Scroll back to top of page
    scrollToTop();
  };

  const content = !isRevising ? (
    <div>
      <h2 className="page-title">Your Essay Graded</h2>
      <FeedbackLegend category={category} />

      {/* Remove inline error message - we're using the modal now */}

      {/* Debug information - will be removed later */}
      {logger.debug('Debug values:', {
        isTeacher: userData?.role === 'teacher',
        userRole: userData?.role,
        parsedFeedbackLength: parsedFeedback.length,
        feedbackType
      })}

      {/* Marking scheme section */}
      {isGCSECategory(category) ? (
        <div className="marking-scheme">
          <h3>GCSE Marking Scheme Explained</h3>
          <p style={{ color: '#9b59b6' }}>
            <strong>AO1 - Argument:</strong> Clear understanding of text, answers the question directly with evidence
          </p>
          <p style={{ color: '#3498db' }}>
            <strong>AO2 - Language:</strong> Analysis of language techniques, dramatic devices, and structure
          </p>
          <p style={{ color: '#f39c12' }}>
            <strong>AO3 - Context:</strong> Relevant historical, social, and literary context connected to the text
          </p>
          <p style={{ color: '#e74c3c' }}>
            <strong>AO4 - Mechanics:</strong> Accurate spelling, punctuation, and varied sentence structure
          </p>
        </div>
      ) : isGCSELanguageCategory(category) ? (
        <div className="marking-scheme">
          <h3> Marking Scheme Explained</h3>
          <p style={{ color: '#f39c12' }}>
            <strong>Content:</strong> Answering all points, expressing opinions, informational content
          </p>
          <p style={{ color: '#3498db' }}>
            <strong>Language:</strong> Complexity of expression. For Q1, includes accuracy considerations (see below).
          </p>
          <p style={{ color: '#e74c3c' }}>
            <strong>Accuracy:</strong> Correctness of spelling, grammar, tenses, gender, etc. (Separately scored for Q2 only)
          </p>
        </div>
      ) : (category === 'Aiste' || category === 'Grammar Checker'|| category.startsWith('Filiocht_') || category === 'IE_Oral') ? (
        <div className="marking-scheme">
          <h3>Marking Scheme Explained</h3>
          <p className="marking-scheme-gaeilge">
            <strong>Gaeilge:</strong> Standard of Irish grammar, vocabulary, etc.
          </p>
          <p className="marking-scheme-content">
            <strong>Content:</strong> Quality of ideas, coherence, etc.
          </p>
          <p className="marking-scheme-style">
            <strong>Style:</strong> Essay structure is appropriate - essay has introduction, body, conclusion
          </p>
        </div>
      ) : (category.startsWith('FR_') || category.startsWith('ES_')) ? (
        <div className="marking-scheme">
          <h3>Marking Scheme Explained</h3>
          <p className="marking-scheme-communication">
            <strong>Communication:</strong> Clear message, appropriate register, coherent structure
          </p>
          <p className="marking-scheme-language-french">
            <strong>Language:</strong> Grammar accuracy, vocabulary range, idiomatic expressions
          </p>
        </div>
      ) : category.startsWith('DE_') ? (
        <div className="marking-scheme">
          <h3>Marking Scheme Explained</h3>
          <p className="marking-scheme-content">
            <strong>Content:</strong> Quality of ideas, coherence, etc.
          </p>
          <p className="marking-scheme-expression">
            <strong>Expression:</strong> Grammar accuracy, vocabulary range, idiomatic expressions
          </p>
        </div>
      ) : (
        <div className="marking-scheme">
          <h3>Marking Scheme Explained</h3>
          <p className="marking-scheme-purpose">
            <strong>Purpose:</strong> Have you answered all parts of the question?
          </p>
          <p className="marking-scheme-coherence">
            <strong>Coherence:</strong> Good points, appropriate quotes, well-structured paragraphs
          </p>
          <p className="marking-scheme-language">
            <strong>Language:</strong> Suitability of tone, style, varied vocabulary
          </p>
          <p className="marking-scheme-mechanics">
            <strong>Mechanics:</strong> Punctuation, spelling, syntax
          </p>
        </div>
      )}

      {/* Question Box */}
      <div className="question-box">
        <h3>Question</h3>
        {isGCSECategory(category) && question.includes("Extract:") ? (
          <>
            <div className="extract-container">
              <h4>Extract</h4>
              <p className="extract-text">{question.split("Question:")[0].replace("Extract:", "").trim()}</p>
            </div>
            <div className="question-text">
              <h4>Question</h4>
              <p>{question.split("Question:")[1].trim()}</p>
            </div>
          </>
        ) : (
          <p>{question}</p>
        )}
      </div>

      {/* Analysis Box */}
      <div className="analysis-container">
        <h3 className="analysis-title">Your Answer Analysis</h3>
        {feedbackType === 'inline' ? (
          <div className="feedback-columns">
            {parsedFeedback.map((paraObj, index) => {
              const { text, feedback } = paraObj;
              const displayParagraph = text.replace(/\\n/g, ' ');
              return (
                <div key={index} className="paragraph-container">
                  {/* LEFT column: paragraph text */}
                  <div className="paragraph-text">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: processInlineFeedback(displayParagraph, category)
                      }}
                    />
                  </div>
                  {/* RIGHT column: feedback */}
                  <div className="feedback-column">
                    {feedback.length > 0 ? (
                      feedback.map((fb, fbIndex) => {
                        const cleanFb = fb.replace(/\\n/g, ' ');
                        const ratingKey = `${index}-${fbIndex}`;
                        const currentRating = commentRatings[ratingKey] || null;
                        const isEditing = isEditingFeedback && 
                                         editableFeedback && 
                                         editableFeedback.paraIndex === index && 
                                         editableFeedback.fbIndex === fbIndex;
                        const isTeacherFeedback = cleanFb.includes('[TEACHER]');
                        // Remove the [TEACHER] marker for display
                        const displayFeedback = cleanFb.replace(/\s*\[TEACHER\]\s*/, '');

                        return (
                          <div key={fbIndex} className="feedback-item">
                            <div className="feedback-row">
                              {/* Comment text or edit field */}
                              {isEditing ? (
                                <div className="feedback-edit-container">
                                  <textarea
                                    value={updatedFeedbackContent}
                                    onChange={(e) => setUpdatedFeedbackContent(e.target.value)}
                                    className="feedback-edit-textarea"
                                    autoFocus
                                  />
                                  <div className="feedback-edit-buttons">
                                    <button 
                                      onClick={handleSaveEditedFeedback}
                                      className="feedback-edit-button save-button"
                                      disabled={feedbackUpdateStatus === 'saving'}
                                    >
                                      {feedbackUpdateStatus === 'saving' ? 'Saving...' : 'Save'}
                                    </button>
                                    <button 
                                      onClick={handleCancelEdit}
                                      className="feedback-edit-button cancel-button"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className={`feedback-content ${isTeacherFeedback ? 'teacher-content' : ''}`}>
                                  {isTeacherFeedback && (
                                    <span className="teacher-badge">Teacher</span>
                                  )}
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: processInlineFeedback(displayFeedback, category)
                                    }}
                                  />
                                </div>
                              )}
                              
                              {/* Teacher edit/delete or user rating buttons */}
                              {userData?.role === 'teacher' ? (
                                <div className="feedback-actions">
                                  <div className="rating-buttons">
                                    <button
                                      onClick={() => handleCommentRatingWithTracking(index, fbIndex, 'up')}
                                      className={`rating-button ${currentRating === 'up' ? 'rating-button-up-selected' : ''}`}
                                    >
                                      👍
                                    </button>
                                    <button
                                      onClick={() => handleCommentRatingWithTracking(index, fbIndex, 'down')}
                                      className={`rating-button ${currentRating === 'down' ? 'rating-button-down-selected' : ''}`}
                                    >
                                      👎
                                    </button>
                                  </div>
                                  <div className="teacher-feedback-controls">
                                    <button
                                      onClick={() => handleDeleteFeedback(index, fbIndex)}
                                      className="teacher-feedback-button delete-button"
                                      title="Delete feedback"
                                    >
                                      🗑️
                                    </button>
                                    <button
                                      onClick={() => handleEditFeedback(index, fbIndex, cleanFb)}
                                      className="teacher-feedback-button edit-button"
                                      title="Edit feedback"
                                    >
                                      ✏️
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="rating-buttons">
                                  <button
                                    onClick={() => handleCommentRatingWithTracking(index, fbIndex, 'up')}
                                    className={`rating-button ${currentRating === 'up' ? 'rating-button-up-selected' : ''}`}
                                  >
                                    👍
                                  </button>
                                  <button
                                    onClick={() => handleCommentRatingWithTracking(index, fbIndex, 'down')}
                                    className={`rating-button ${currentRating === 'down' ? 'rating-button-down-selected' : ''}`}
                                  >
                                    👎
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="no-feedback-message">
                        No separate feedback for this paragraph
                      </div>
                    )}
                    
                    {/* Add feedback form */}
                    {isAddingFeedback && addFeedbackParaIndex === index ? (
                      <div className="add-feedback-container">
                        <div className="add-feedback-type-selector">
                          <label>Feedback Type:</label>
                          <select
                            value={newFeedbackType}
                            onChange={(e) => setNewFeedbackType(e.target.value)}
                            className="add-feedback-select"
                          >
                            {getFeedbackTypeOptions().map((option) => (
                              <option 
                                key={option.value} 
                                value={option.value}
                                style={{ color: option.color }}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <textarea
                          value={newFeedbackContent}
                          onChange={(e) => setNewFeedbackContent(e.target.value)}
                          placeholder="Enter new feedback..."
                          className="add-feedback-textarea"
                        />
                        <div className="add-feedback-buttons">
                          <button 
                            onClick={handleSaveNewFeedback}
                            className="add-feedback-button save-button"
                            disabled={feedbackUpdateStatus === 'saving'}
                          >
                            {feedbackUpdateStatus === 'saving' ? 'Saving...' : 'Add Feedback'}
                          </button>
                          <button 
                            onClick={handleCancelAddFeedback}
                            className="add-feedback-button cancel-button"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      userData?.role === 'teacher' && (
                        <button
                          onClick={() => handleAddFeedback(index)}
                          className="add-feedback-button-small"
                        >
                          + Add Feedback
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="paragraph-text">
            {trimmedResult.replace(/\\n/g, ' ')}
          </div>
        )}
      </div>

      {/* Grade section - only show for English categories */}
      {isEnglishCategory(category) && gradingText && (
        <div className="grade-container">
          <div className="grade-header-row">
            <h3 className="grade-title">Grade Calculation</h3>
            {userData?.role === 'teacher' && !isEditingGrade && (
              <button
                onClick={handleEditGrade}
                className="teacher-grade-edit-button"
                title="Edit grade"
              >
                ✏️ Edit Grade
              </button>
            )}
          </div>
          
          {/* Add word count warning above the grade */}
          {getWordCountWarning()}
          
          {isEditingGrade ? (
            <div className="grade-edit-container">
              <textarea
                value={editedGradeText}
                onChange={(e) => setEditedGradeText(e.target.value)}
                className="grade-edit-textarea"
                rows={10}
              />
              <div className="grade-edit-buttons">
                <button 
                  onClick={handleSaveGrade}
                  className="grade-edit-button save-button"
                  disabled={gradeSaveStatus === 'saving'}
                >
                  {gradeSaveStatus === 'saving' ? 'Saving...' : 'Save Grade'}
                </button>
                <button 
                  onClick={handleCancelGradeEdit}
                  className="grade-edit-button cancel-button"
                >
                  Cancel
                </button>
              </div>
              {gradeSaveStatus && (
                <div className={`grade-save-status grade-save-${gradeSaveStatus}`}>
                  {gradeSaveStatus === 'success' && '✓ Saved successfully'}
                  {gradeSaveStatus === 'error' && '⚠ Error saving'}
                </div>
              )}
            </div>
          ) : (
            <div className="grade-text"
              dangerouslySetInnerHTML={{ 
                __html: formatGradeText(displayedGradeText)
              }}
            />
          )}
          
          <div className="grade-caveat">
            <p>
              <strong>Estimated Grade:</strong> This is an estimated grade. Pulc is often a harsh marker and sometimes
              makes mistakes. Always rely on your teacher's grade and feedback first. For some FAQs
              about estimated grades, see <button onClick={toggleFAQ} className="faq-link">here</button>.
            </p>
          </div>
        </div>
      )}

      {/* Teacher feedback section */}
      {userData?.role === 'teacher' ? (
        <div className="teacher-input-container">
          <h3 className="teacher-input-title">Teacher Feedback</h3>
          {teacherFeedbackSubmitted ? (
            <div className="teacher-input-success">
              <p className="teacher-input-success-title">Feedback submitted ✓</p>
              <p className="teacher-input-success-text">{teacherFeedbackInput}</p>
            </div>
          ) : (
            <>
              <div style={{ marginTop: '20px' }}>
                <textarea
                  value={teacherFeedbackInput}
                  onChange={(e) => setTeacherFeedbackInput(e.target.value)}
                  placeholder="Here you can add your own feedback on the essay, and give a grade if you wish."
                  spellCheck="false"
                  className="common-input"
                  style={{ minHeight: '150px', boxSizing: 'border-box' }}
                />
                <div className="teacher-input-buttons">
                  <button
                    onClick={handleSaveTeacherFeedback}
                    disabled={feedbackSaveStatus === 'saving'}
                    className="save-feedback-button"
                  >
                    {feedbackSaveStatus === 'saving' ? 'Saving...' : 'Save Teacher Feedback'}
                  </button>
                  {feedbackSaveStatus && (
                    <span className={`save-feedback-status save-feedback-${feedbackSaveStatus}`}>
                      {feedbackSaveStatus === 'success' && '✓ Saved successfully'}
                      {feedbackSaveStatus === 'error' && '⚠ Error saving'}
                      {feedbackSaveStatus === 'saving' && 'Saving...'}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        teacherFeedback && (
          <div className="teacher-feedback-container">
            <h3 className="teacher-feedback-title">Teacher Feedback</h3>
            <p className="teacher-feedback-text">
              {teacherFeedback || initialTeacherFeedback}
            </p>
          </div>
        )
      )}

      {/* FAQ Popup */}
      {showFAQ && (
        <div className="faq-modal-overlay">
          <div className="faq-modal">
            <div className="faq-modal-header">
              <h2>FAQ – Grading</h2>
              <button onClick={toggleFAQ} className="faq-close-button">×</button>
            </div>
            <div className="faq-modal-content">
              <p>
                Pulc is still developing and can sometimes be a bit unreliable with its grading. Below you can
                find answers to some common questions.
                Pulc's grade is always an estimate – always trust your teacher's grade first.
              </p>
              
              <div className="faq-question">
                <button 
                  className="faq-question-header" 
                  onClick={() => toggleQuestion('reliability')}
                >
                  <h3>Q. How reliable is my grade?</h3>
                  <span className="faq-toggle-icon">
                    {expandedQuestions.reliability ? '−' : '+'}
                  </span>
                </button>
                
                {expandedQuestions.reliability && (
                  <div className="faq-question-content">
                    <p>
                      Pulc is trained specifically for the exam and grades according to the marking scheme for the
                      specific question. However, Pulc can be a harsh marker and its grades can sometimes be up to 10% off.
                    </p>
                    <p>
                      The grade is usually quite reliable, but can sometimes be wrong in isolated cases. If you think
                      that's the case, just resubmit — you should get a more accurate version.
                    </p>
                    <p>Remember, Pulc's grade is only an estimate. Always rely on your teacher's grade first.</p>
                    <p>
                      If, after trying a second time, you still think the grade is wrong, let us know <button className="faq-link" onClick={() => {toggleFAQ(); document.getElementById('feedback-section').scrollIntoView({behavior: 'smooth'});}}>here</button>.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="faq-question">
                <button 
                  className="faq-question-header" 
                  onClick={() => toggleQuestion('lowGrade')}
                >
                  <h3>Q. My grade seems very low.</h3>
                  <span className="faq-toggle-icon">
                    {expandedQuestions.lowGrade ? '−' : '+'}
                  </span>
                </button>
                
                {expandedQuestions.lowGrade && (
                  <div className="faq-question-content">
                    <p>
                      Don't panic. Pulc can be a slightly harsh marker, and its grades can sometimes be up to 10% off. 
                    </p>
                    <p>
                      Please don't let your Pulc grade put you under any pressure. Ask your teacher if you think the
                      grade is very low.
                    </p>
                    <p>
                      If you feel the grade is unusually low, Pulc might have been confused. In that case, we
                      recommend submitting the essay again. It can also help to correct spelling errors, as these can
                      sometimes disrupt Pulc's analysis.
                    </p>
                    <p>
                      Let us know if you had trouble with your grade by clicking <button className="faq-link" onClick={() => {toggleFAQ(); document.getElementById('feedback-section').scrollIntoView({behavior: 'smooth'});}}>here</button>.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="faq-question">
                <button 
                  className="faq-question-header" 
                  onClick={() => toggleQuestion('highGrade')}
                >
                  <h3>Q. My grade seems very high.</h3>
                  <span className="faq-toggle-icon">
                    {expandedQuestions.highGrade ? '−' : '+'}
                  </span>
                </button>
                
                {expandedQuestions.highGrade && (
                  <div className="faq-question-content">
                    <p>
                      If your grade appears overly high, Pulc might have made an error in marking. Resubmit your
                      essay and you'll likely get a more accurate result.
                    </p>
                    <p>
                      One common issue is that Pulc gives generous grades to essays that are too short. In its current
                      version, Pulc is designed to grade full-length Leaving Cert answers. If your essay is short,
                      don't rely on the mark – write a longer version and see what grade you get.
                    </p>
                    <p>
                      If you think your grade is too high, let us know by clicking <button className="faq-link" onClick={() => {toggleFAQ(); document.getElementById('feedback-section').scrollIntoView({behavior: 'smooth'});}}>here</button>.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="faq-question">
                <button 
                  className="faq-question-header" 
                  onClick={() => toggleQuestion('different')}
                >
                  <h3>Q. I re-submitted the same essay and got a different grade.</h3>
                  <span className="faq-toggle-icon">
                    {expandedQuestions.different ? '−' : '+'}
                  </span>
                </button>
                
                {expandedQuestions.different && (
                  <div className="faq-question-content">
                    <p>
                      This can happen with Pulc — but it also happens with humans. There is often some variation
                      in the grade a teacher might give for the same assignment, or the grade you might get from two different teachers.
                    </p>
                    <p>
                      If your two grades are more than 10% apart, it's worth submitting your answer once more.
                      Whichever two grades are closest likely reflect the true level of your essay.
                    </p>
                    <p>
                      Remember that the Pulc grade is an estimate and should be used to give you a broad sense of
                      what level your essay is at.
                    </p>
                    <p>
                      If you think something is wrong with your grade, let us know <button className="faq-link" onClick={() => {toggleFAQ(); document.getElementById('feedback-section').scrollIntoView({behavior: 'smooth'});}}>here</button>.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="faq-question">
                <button 
                  className="faq-question-header" 
                  onClick={() => toggleQuestion('worse')}
                >
                  <h3>Q. I followed Pulc's feedback and got a worse grade on resubmitting.</h3>
                  <span className="faq-toggle-icon">
                    {expandedQuestions.worse ? '−' : '+'}
                  </span>
                </button>
                
                {expandedQuestions.worse && (
                  <div className="faq-question-content">
                    <p>
                      Don't worry. Pulc's feedback is generally reliable and should help improve your essay.
                    </p>
                    <p>
                      A lower grade is usually just a result of variation in grading or Pulc's strict marking. It
                      doesn't mean your essay has actually gotten worse.
                    </p>
                    <p>
                      Though it's tempting to focus only on the grade, we encourage you to focus on the feedback
                      — that's what will improve your current and future answers.
                    </p>
                    <p>
                      Let us know if you had trouble with your grade by clicking <button className="faq-link" onClick={() => {toggleFAQ(); document.getElementById('feedback-section').scrollIntoView({behavior: 'smooth'});}}>here</button>.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="faq-question">
                <button 
                  className="faq-question-header" 
                  onClick={() => toggleQuestion('wrongMarks')}
                >
                  <h3>Q. My answer was graded out of the wrong number of marks for the question.</h3>
                  <span className="faq-toggle-icon">
                    {expandedQuestions.wrongMarks ? '−' : '+'}
                  </span>
                </button>
                
                {expandedQuestions.wrongMarks && (
                  <div className="faq-question-content">
                    <p>
                      This can sometimes happen, but it shouldn't affect the quality of the feedback. Most
                      importantly, the overall percentage grade you're given should still be broadly correct. It's
                      just Pulc getting confused about which number to display at the end.
                    </p>
                    <p>
                      If this happens, please let us know via the <button className="faq-link" onClick={() => {toggleFAQ(); document.getElementById('feedback-section').scrollIntoView({behavior: 'smooth'});}}>feedback form</button> and we'll work on it.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revise button */}
      {feedbackType === 'inline' && (
        <div className="result-revise-button-container">
          <button
            onClick={handleStartRevisionWithTracking}
            className={`result-revise-button ${
              (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral')
                ? 'irish-revise-button'
                : ''
            }`}
          >
            Revise Essay
          </button>
        </div>
      )}

      {/* Action buttons */}
      <div className="action-buttons-container">
        <button onClick={handleDownloadWithTracking} className="common-button secondary-button">
          Download Feedback
        </button>

        <button onClick={handleDownloadOriginalWithTracking} className="common-button secondary-button">
          Download Original Answer
        </button>

        <button onClick={handleResetApp} className="common-button secondary-button">
          New Essay
        </button>
      </div>

      {/* Feedback form */}
      <div className="feedback-section" id="feedback-section">
        <h3>Leave Feedback on Pulc</h3>
        {!feedbackSubmitted ? (
          <form onSubmit={handleSubmitFeedbackWithTracking} className="feedback-form">
            <label className="feedback-label">
              Feedback Category:
              <select
                value={feedbackCategory}
                onChange={(e) => setFeedbackCategory(e.target.value)}
                className="common-input feedback-select"
                required
              >
                <option value="Feedback on the App Functionality">Feedback on the App Functionality</option>
                <option value="Feedback on my Results">Feedback on my Results</option>
                <option value="Suggest a Feature">Suggest a Feature</option>
                <option value="Other">Other</option>
              </select>
            </label>

            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Please share your thoughts on the feedback you received..."
              spellCheck="false"
              className="common-input"
              style={{ minHeight: '150px' }}
            />
            <button type="submit" className="common-button">
              Tell us your thoughts!
            </button>
          </form>
        ) : (
          <p>Thank you for your feedback!</p>
        )}
      </div>
    </div>
  ) : (
    ////////////////////////////////////////////////////////////////////////
    // REVISE MODE
    ////////////////////////////////////////////////////////////////////////
    <div className="revise-container">
      <h2>Revise Your Essay</h2>
      <FeedbackLegend category={category} />
      <p>Make any edits to your essay below, then click "Confirm Revision" when you're finished.</p>
      
      {/* Replace the existing mapping logic with our new function */}
      {renderRevisionContent(result)}
      
      <div className="revise-buttons">
        <button
          onClick={handleConfirmClick}
          className={`confirm-revision-button ${
            (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral')
              ? 'confirm-revision-button-irish'
              : 'confirm-revision-button-english'
          }`}
        >
          Confirm Revision
        </button>
        <button
          onClick={() => {
            // Track analytics for cancellation
            track('revision_cancelled', { 
              category: category
            });
            
            // Exit revision mode without saving changes
            setIsRevising(false);
            scrollToTop();
          }}
          className="cancel-revision-button"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="content-wrapper">
      {/* Error Modal */}
      <ErrorModal 
        message={error}
        onClose={() => setError(null)}
      />
      {content}
    </div>
  );
}
